import { useLayoutEffect, useRef } from 'react';
import { resolveSection, isKeepAlive } from './routeMap';
import { nextSlots, hostSlotStyle, type MountedSlot } from './pageHostState';
import { nextActivationTicks, type ActivationTicks } from './activation';
import { ActivationTickProvider } from '../lib/ActivationContext';

// The page host (keep-alive-page-host AC1/AC2/AC3/AC5). Given the active nav
// key, it keeps every visited keep-alive page mounted and toggles visibility
// (display:none on inactive) instead of unmounting — so a page's component
// instance and local state (filters, pagination, scroll) survive a nav round
// trip. A route can opt out via `remountOnReturn` in the map (AC5): such a page
// is mounted only while active and re-mounts fresh on return.
//
// On every *return* to a kept page the host bumps that page's activation tick
// and provides it down through ActivationTickContext; the page's data hook
// reads the tick and fires a silent background re-fetch (AC3/AC4) — no per-page
// wiring, inherited by registration. Future-registered pages get keep-alive +
// revalidate for free (AC2), driven by the route map alone.
//
// State lives in refs, reconciled during render and keyed on the active view.
// AppShell re-renders the host whenever `view` (its own state) changes, so the
// reconciliation runs on every nav with no extra setState — the returning page
// sees its bumped tick on the same paint, so there is no blank frame. The
// `prevView` guard makes the mutation idempotent under StrictMode's double
// render-invoke (the second pass sees prevView === view and is a no-op).
export function PageHost({ view }: { view: string }) {
  const slotsRef = useRef<MountedSlot[]>([]);
  const ticksRef = useRef<ActivationTicks>({});
  const prevViewRef = useRef<string | null>(null);

  // Per-page scroll memory. The comment above promises a page's scroll survives a
  // nav round trip — it did not. Pages are toggled with display:none, but the
  // SCROLL lives on the document and is shared by all of them, so it never belonged
  // to a page at all: it simply leaked. Scroll Bookings down, click Rota, and Rota
  // opened at Bookings' offset, hundreds of pixels past its own heading.
  //
  // (It only bit on a WARM target. Hiding the outgoing page collapses the document
  // to the height of a cold, still-rendering incoming one, and the browser clamps
  // the scroll to 0 — which is why this looked fine on a first visit and broke on
  // the way back.)
  //
  // Save the outgoing page's offset during render, BEFORE the DOM swaps and while
  // window.scrollY is still its own; restore the incoming page's in a layout effect
  // once its slot is visible. A page never visited restores to 0 — so every page now
  // opens at the top, and returns to where you left it.
  const scrollByViewRef = useRef<Record<string, number>>({});
  const pendingScrollRef = useRef<number | null>(null);

  if (prevViewRef.current !== view) {
    if (prevViewRef.current !== null) {
      scrollByViewRef.current[prevViewRef.current] = window.scrollY;
    }
    pendingScrollRef.current = scrollByViewRef.current[view] ?? 0;

    const wasMounted = slotsRef.current.some((s) => s.key === view);
    slotsRef.current = nextSlots(slotsRef.current, view, isKeepAlive(view));
    // Bump the tick only when returning to an already-mounted (kept) page. A
    // first mount does not bump (it fetches on mount); a remount-on-return page
    // is never already-mounted when re-activated, so it never bumps — it
    // re-fetches via its fresh mount instead (AC5).
    ticksRef.current = nextActivationTicks(ticksRef.current, wasMounted, view);
    prevViewRef.current = view;
  }

  // Runs after the slot's display flips, so the target height exists to scroll to.
  // A deliberate jump (Rota's On-duty "view all") still wins: it scrolls in a
  // double rAF, which lands after every layout effect.
  useLayoutEffect(() => {
    const y = pendingScrollRef.current;
    if (y === null) return;
    pendingScrollRef.current = null;
    window.scrollTo(0, y);
  });

  return (
    <>
      {slotsRef.current.map((slot) => {
        const { Component } = resolveSection(slot.key);
        const tick = ticksRef.current[slot.key] ?? 0;
        return (
          <div key={slot.key} className="page-slot" style={hostSlotStyle(slot.key, view)}>
            <ActivationTickProvider value={tick}>
              <Component />
            </ActivationTickProvider>
          </div>
        );
      })}
    </>
  );
}
